/**
 * Default avatar helper — uses 4 real, photo-style AI portrait avatars (2 male,
 * 2 female) bundled with the app at /avatars/*.png.  This gives Qrew HR a real,
 * professional look (not illustrated cartoons).
 *
 * Picking rules:
 *  1. If the employee has a custom `avatar_url` (uploaded photo), use it.
 *  2. Else, pick male/female based on `gender` field; fall back to a hash of
 *     the first name to guess.
 *  3. Within each gender, pick variant 1 or 2 deterministically by hashing the
 *     employee id / email / name → so the same person always sees the same face
 *     and a small team gets a mix instead of everyone looking identical.
 */

const FEMALE_AVATARS = ["/avatars/female-1.png", "/avatars/female-2.png"];
const MALE_AVATARS   = ["/avatars/male-1.png",   "/avatars/male-2.png"];

const FEMALE_NAME_HINTS = [
  "anya","diya","priya","neha","maya","aisha","sara","lina","tara","ria","ishika",
  "kavya","mira","aarya","ananya","pooja","sneha","shreya","riya","nisha","meera",
  "asha","deepa","geeta","hema","jaya","kiran","lata","manisha","nita","preeti",
  "radha","seema","tanvi","usha","vandana","kavita","sunita","vidya","ritu","anjali",
];
const MALE_NAME_HINTS = [
  "aarav","vihaan","arjun","rohan","raj","ajay","amit","akhil","kiran","manish",
  "vikram","ravi","ankit","rahul","mohit","vivek","sandeep","suresh","abhishek",
  "alok","bhavesh","chetan","dev","gaurav","hardik","ishan","jatin","karan","lokesh",
  "mukesh","nitin","om","pranav","rakesh","sachin","tarun","ujjwal","varun","yash",
];

function hash(str = "") {
  let h = 0;
  const s = String(str);
  for (let i = 0; i < s.length; i++) h = ((h * 31) + s.charCodeAt(i)) >>> 0;
  return h;
}

function isFemaleHint(emp) {
  const g = (emp.gender || "").toLowerCase();
  if (g === "female" || g === "f") return true;
  if (g === "male" || g === "m") return false;
  const first = String(emp.full_name || emp.first_name || emp.name || "")
    .trim().toLowerCase().split(/\s+/)[0];
  if (FEMALE_NAME_HINTS.includes(first)) return true;
  if (MALE_NAME_HINTS.includes(first)) return false;
  // Deterministic 50/50 fallback
  return hash(first) % 2 === 0;
}

export function avatarFor(emp) {
  if (!emp) return FEMALE_AVATARS[0];
  if (emp.avatar_url && !emp.avatar_url.includes("dicebear.com")) return emp.avatar_url;
  const pool = isFemaleHint(emp) ? FEMALE_AVATARS : MALE_AVATARS;
  const seed = emp.id || emp.email || emp.full_name || "qrew";
  return pool[hash(seed) % pool.length];
}

// Convenience helpers used elsewhere
export function maleAvatar()   { return MALE_AVATARS[0]; }
export function femaleAvatar() { return FEMALE_AVATARS[0]; }

export function initialsOf(name = "U") {
  return String(name).split(" ").filter(Boolean).slice(0, 2)
    .map((s) => s[0]?.toUpperCase()).join("");
}
